import { useRef, useState, useEffect } from 'react';
import { Button } from './ui/button';
import {
  Bold, Italic, Heading2, List, Link2, Code2, Eye, Underline as UnderlineIcon,
} from 'lucide-react';

const TOKENS = [
  { token: '{{first_name}}', label: 'First name' },
  { token: '{{company}}', label: 'Company' },
  { token: '{{full_name}}', label: 'Full name' },
  { token: '{{email}}', label: 'Email' },
];

const ToolbarBtn = ({ onClick, title, children, testId }) => (
  <button
    type="button"
    title={title}
    data-testid={testId}
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className="w-8 h-8 inline-flex items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
  >
    {children}
  </button>
);

/**
 * Lightweight HTML email editor: a contentEditable WYSIWYG with a basic toolbar,
 * a raw-HTML source toggle, personalization-token chips, and a live preview.
 * Controlled via `value` (html string) / `onChange`.
 */
export const HtmlEmailEditor = ({ value, onChange, disabled }) => {
  const editorRef = useRef(null);
  const htmlRef = useRef(null);
  const [mode, setMode] = useState('visual'); // visual | html | preview

  // Keep the contenteditable in sync when value changes externally (e.g. AI draft)
  useEffect(() => {
    if (mode === 'visual' && editorRef.current && editorRef.current.innerHTML !== (value || '')) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value, mode]);

  const exec = (command, arg = null) => {
    if (disabled) return;
    editorRef.current?.focus();
    document.execCommand(command, false, arg);
    onChange(editorRef.current.innerHTML);
  };

  const insertLink = () => {
    const url = window.prompt('Link URL (https://…)');
    if (url) exec('createLink', url);
  };

  const insertToken = (token) => {
    if (disabled) return;
    if (mode === 'html') {
      const ta = htmlRef.current;
      const start = ta.selectionStart ?? (value || '').length;
      const next = (value || '').slice(0, start) + token + (value || '').slice(start);
      onChange(next);
      return;
    }
    editorRef.current?.focus();
    document.execCommand('insertText', false, token);
    onChange(editorRef.current.innerHTML);
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white" data-testid="html-email-editor">
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-gray-100 bg-gray-50/60 flex-wrap">
        <ToolbarBtn onClick={() => exec('bold')} title="Bold" testId="editor-bold"><Bold className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec('italic')} title="Italic" testId="editor-italic"><Italic className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec('underline')} title="Underline"><UnderlineIcon className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec('formatBlock', '<h2>')} title="Heading"><Heading2 className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec('insertUnorderedList')} title="Bullet list"><List className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={insertLink} title="Insert link"><Link2 className="w-4 h-4" /></ToolbarBtn>
        <div className="w-px h-5 bg-gray-200 mx-1" />
        <button type="button" onClick={() => setMode(mode === 'html' ? 'visual' : 'html')}
          className={`h-8 px-2 inline-flex items-center gap-1 rounded-md text-xs font-medium transition-colors ${mode === 'html' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
          data-testid="editor-html-toggle">
          <Code2 className="w-3.5 h-3.5" /> HTML
        </button>
        <button type="button" onClick={() => setMode(mode === 'preview' ? 'visual' : 'preview')}
          className={`h-8 px-2 inline-flex items-center gap-1 rounded-md text-xs font-medium transition-colors ${mode === 'preview' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
          data-testid="editor-preview-toggle">
          <Eye className="w-3.5 h-3.5" /> Preview
        </button>
      </div>

      {/* Token chips */}
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-gray-100 flex-wrap">
        <span className="text-[11px] text-gray-400 mr-1">Insert:</span>
        {TOKENS.map((t) => (
          <button key={t.token} type="button" onClick={() => insertToken(t.token)}
            className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors font-medium"
            data-testid={`token-${t.token.replace(/[{}]/g, '')}`}
            title={`Insert ${t.label}`}>
            {t.token}
          </button>
        ))}
      </div>

      {/* Editing surface */}
      {mode === 'html' ? (
        <textarea
          ref={htmlRef}
          value={value || ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-72 p-4 font-mono text-xs text-gray-800 outline-none resize-y"
          placeholder="<p>Your HTML here…</p>"
          data-testid="editor-html-source"
        />
      ) : mode === 'preview' ? (
        <div className="p-4 bg-gray-100">
          <div className="max-w-[600px] mx-auto bg-white rounded-lg p-6 shadow-sm prose prose-sm"
            data-testid="editor-preview"
            dangerouslySetInnerHTML={{ __html: value || '<p style="color:#94a3b8">Nothing to preview yet.</p>' }} />
        </div>
      ) : (
        <div
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={(e) => onChange(e.currentTarget.innerHTML)}
          className="min-h-72 max-h-[28rem] overflow-y-auto p-4 text-sm text-gray-800 outline-none leading-relaxed prose prose-sm max-w-none focus:ring-0"
          data-testid="editor-visual"
        />
      )}
    </div>
  );
};

export default HtmlEmailEditor;
